import * as React from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { makeStyles, tokens } from '@fluentui/react-components';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
// Import centralized interfaces
import type { MarkdownMessageProps } from '../interfaces';
import TicketCard from './TicketCard';
import { rehypeSanitizePlugin } from '../../../utils/htmlSanitizer';

// WI-1249082: Allow create-ticket-action:// scheme through ReactMarkdown v9 URL sanitizer
// WI-1248753: Allow escalate-action:// scheme for one-click escalation from open tickets list
const customUrlTransform = (url: string) => {
  if (url.startsWith('create-ticket-action://')) return url;
  if (url.startsWith('escalate-action://')) return url;
  if (url.startsWith('ticket-card://')) return url;
  if (url.startsWith('send-prompt-action://')) return url;
  return defaultUrlTransform(url);
};

// WI-1248753: Strip redundant sub-bullets from escalate ticket lines.
// Old API format emits bullet header + sub-bullets; new API emits only the link.
// This pre-processor handles both — removes "- **INC..** (SNOW)\t[Escalate](...)"
// bullet wrapper and trailing sub-bullets, leaving just the [Escalate](...) link.
const stripEscalateSubBullets = (md: string): string => {
  // Pattern: "- **INCxxx** (SNOW)\t[Escalate](escalate-action://...)\r\n  - sub-bullet..."
  // Replace entire bullet+sub-bullets with just the escalate link on its own line
  return md.replace(
    /^- \*\*[^*]+\*\* \(SNOW\)\t(\[Escalate\]\(escalate-action:\/\/[^)]+\))(?:\r?\n(?:  - .*)?)*/gm,
    '$1'
  );
};

// WI-1249082: Lazy App Insights singleton — avoids duplicate instantiation per import
let _appInsights: ApplicationInsights | null = null;
function getAppInsights(): ApplicationInsights {
  if (!_appInsights) {
    _appInsights = new ApplicationInsights({
      config: { instrumentationKey: process.env.REACT_APP_INSTRUMENTATION_KEY }
    });
    _appInsights.loadAppInsights();
  }
  return _appInsights;
}

// WI-1249082: Module-level flag — replaces window.__selfHelpButtonShown global
// Shared between MarkdownMessage (sets) and useMessageSender (reads/clears)
export let selfHelpButtonShown = false;
export function setSelfHelpButtonShown(val: boolean) { selfHelpButtonShown = val; }


// WI-1249082: Listen for deflection success event from useMessageSender
// (user saw self-help button but chose to continue chatting instead of creating ticket)
window.addEventListener('self-help-deflection-success', ((e: CustomEvent) => {
  getAppInsights().trackEvent({
    name: 'SELF_HELP_DEFLECTION_SUCCESS',
    properties: {
      nextQuery: e.detail?.nextQuery || '',
      timestamp: e.detail?.timestamp || new Date().toISOString(),
    }
  });
}) as EventListener);

const useStyles = makeStyles({
  markdownMessage: {
    fontSize: '14px !important',
    lineHeight: 1.4,
    color: '#323130',
    fontFamily: 'Segoe UI, "Segoe UI Web (West European)", Arial, sans-serif',
    width: '100%',

    // Headings (legacy-like spacing & sizing)
    '& h1': {
      fontSize: '20px',
      fontWeight: '600',
      margin: '0 0 12px',
      lineHeight: '24px'
    },
    '& h2': {
      fontSize: '16px',
      fontWeight: '600',
      margin: '0 0 12px',
      lineHeight: '20px'
    },
    '& h3': {
      fontSize: '14px',
      fontWeight: '600',
      margin: '0 0 8px',
      lineHeight: '20px'
    },
    '& h4, & h5, & h6': {
      fontSize: '14px',
      fontWeight: '400',
      margin: '0 0 8px',
      lineHeight: '20px'
    },
    '& p': {
      margin: '0 0 16px',
      lineHeight: '20px',
      fontSize: '14px'
    },

    // Lists
    '& ol': {
      listStyleType: 'decimal',
      paddingLeft: '20px',
      margin: '0 0 8px'
    },
    '& ul': {
      paddingLeft: '20px',
      margin: '0 0 8px'
    },
    '& li': {
      marginBottom: 0
    },
    '& li:last-child': {
      marginBottom: 0
    },

    // Tables (scoped, no global injection needed)
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      margin: '16px 0',
      tableLayout: 'auto'
    },
    '& th, & td': {
      border: '1px solid #000',
      padding: '5px',
      textAlign: 'left',
      fontSize: '12px',
      lineHeight: 1.4,
      verticalAlign: 'top'
    },
    '& th': {
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      height: '54px',
      textTransform: 'uppercase',
      verticalAlign: 'middle',
      textShadow: '0px 1px 0px #ffffff',
      fontSize: '10px',
      minWidth: '50px'
    },

    '& code': {
      background: tokens.colorNeutralBackground2,
      padding: '2px 4px',
      borderRadius: '4px',
      fontSize: '12px'
    },
    '& pre': {
      background: tokens.colorNeutralBackground2,
      padding: '8px 12px',
      borderRadius: '4px',
      fontSize: '12px',
      lineHeight: 1.4
    },
    '& b, & strong': {
      fontWeight: '600 !important'
    }
  },

  link: {
    color: '#0d6efd',
    textDecoration: 'underline'
  },

  // Grey banner style for "How to respond"
  howToRespondBanner: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '6px',
    padding: '12px 14px',
    marginTop: '8px',
    marginBottom: '8px',
    color: tokens.colorNeutralForeground3,
    '& > p:first-of-type': {
      color: tokens.colorNeutralForeground3,
      fontWeight: 600
    }
  }
});

const normalizeLineBreaks = (value: string): string =>
  value.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\r\n/g, '\n').trim();

const normalizeHeaderText = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^💡\s*/, '')
    .replace(/\s+/g, ' ');

const isBannerHeader = (value: string): boolean => {
  const text = normalizeHeaderText(value).replace(/\*\*/g, '');
  return (
    text === 'how to respond:' ||
    text === 'how to respond' ||
    text === 'need more help?' ||
    text === 'need more help'
  );
};

type MarkdownSegment = { type: 'content' | 'banner'; text: string };

/** Split markdown into alternating content/banner segments so each
 *  "Need more help?" / "How to respond" block is greyed independently. */
const parseSegments = (normalizedMd: string): MarkdownSegment[] => {
  const lines = normalizedMd.split('\n');
  const segments: MarkdownSegment[] = [];
  let i = 0;

  while (i < lines.length) {
    if (isBannerHeader(lines[i])) {
      const bannerLines: string[] = [lines[i]];
      i++;
      while (i < lines.length) {
        if (lines[i].trim() === '---') {
          // Look ahead past empty lines to see if next content is non-banner
          let j = i + 1;
          while (j < lines.length && lines[j].trim() === '') j++;
          if (j >= lines.length || !isBannerHeader(lines[j])) {
            break; // End of this banner section
          }
        }
        bannerLines.push(lines[i]);
        i++;
      }
      const text = bannerLines.join('\n').trim();
      if (text) segments.push({ type: 'banner', text });
      if (i < lines.length && lines[i].trim() === '---') i++; // skip closing ---
    } else {
      const contentLines: string[] = [lines[i]];
      i++;
      while (i < lines.length && !isBannerHeader(lines[i])) {
        contentLines.push(lines[i]);
        i++;
      }
      const text = contentLines.join('\n').trim();
      if (text) segments.push({ type: 'content', text });
    }
  }

  return segments;
};

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ markdown, ticketGuidance }) => {
  const styles = useStyles();
  const containerRef = React.useRef<HTMLDivElement>(null);
  // WI-1249082: Track whether create-ticket button rendered (set via useEffect, not during render)
  const buttonRenderedRef = React.useRef(false);
  const normalizedMarkdown = React.useMemo(
    () => stripEscalateSubBullets(typeof markdown === 'string' ? normalizeLineBreaks(markdown) : String(markdown ?? '')),
    [markdown]
  );
  const normalizedTicketGuidance = React.useMemo(() => {
    if (typeof ticketGuidance !== 'string') {
      return '';
    }

    return normalizeLineBreaks(ticketGuidance);
  }, [ticketGuidance]);
  const ticketGuidanceLeadLine = React.useMemo(
    () =>
      normalizedTicketGuidance
        .split('\n')
        .find((line) => line.trim().length > 0)
        ?.trim() || '',
    [normalizedTicketGuidance]
  );
  const segments = React.useMemo(
    () => parseSegments(normalizedMarkdown),
    [normalizedMarkdown]
  );
  const hasBannerSegments = segments.some((s) => s.type === 'banner');
  const shouldRenderTicketGuidanceBanner =
    Boolean(normalizedTicketGuidance) &&
    !hasBannerSegments &&
    !(
      (ticketGuidanceLeadLine && normalizedMarkdown.includes(ticketGuidanceLeadLine)) ||
      normalizedMarkdown.includes(normalizedTicketGuidance)
    );

  // Removed global style injection – styles consolidated in makeStyles for predictable cascade
  // Force table re-layout when container size changes (similar to legacy behavior)
  React.useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver((entries) => {
        // Force table to recalculate layout by triggering a minimal DOM change
        entries.forEach(() => {
          const tables = containerRef.current?.querySelectorAll('table');
          tables?.forEach((table) => {
            // Force reflow by temporarily changing and restoring a style
            const originalDisplay = table.style.display;
            table.style.display = 'none';
            // Force reflow - assign to variable to avoid ESLint warning
            const height = table.offsetHeight;
            void height; // Explicitly mark as intentionally unused
            table.style.display = originalDisplay;
          });
        });
      });

      // Find the parent panel container to observe
      let element = containerRef.current.parentElement;
      while (element && !element.classList.contains('ms-Panel-main')) {
        element = element.parentElement;
      }
      if (element) {
        resizeObserver.observe(element);
      }
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [markdown]); // Re-run when markdown content changes

  // WI-1249082: Sync buttonRenderedRef to module-level flag AFTER render (not during)
  // Prevents false deflection telemetry caused by re-renders re-setting the flag
  React.useEffect(() => {
    if (buttonRenderedRef.current) {
      setSelfHelpButtonShown(true);
    }
  });

  // WI-1249082: LinkRenderer detects create-ticket-action:// URL scheme
  // Renders as blue button for selfHelpOnlyMode tiles, dispatches CustomEvent to open form panel
  const LinkRenderer = ({ href, children }: any) => {
    // WI-1248753: Escalate action link — renders as a styled card (Figma V3 design)
    if (href && href.startsWith('escalate-action://')) {
      const queryString = href.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      const ticketId = params.get('ticketId') || '';
      const title = params.get('title') || '--';
      const state = params.get('state') || 'Active';
      const ticketLink = params.get('link') || '';
      const createdOn = params.get('createdOn') || '';

      return (
        <TicketCard
          ticketId={ticketId}
          state={state}
          title={title}
          ticketLink={ticketLink}
          createdOn={createdOn}
          source="SNOW"
          actionLabel="Escalate"
          onAction={(id) => {
            window.dispatchEvent(new CustomEvent('copilot-escalate-ticket', { detail: { ticketId: id } }));
          }}
        />
      );
    }

    // WI-1248753: View-only ticket card (no escalate action button)
    if (href && href.startsWith('ticket-card://')) {
      const queryString = href.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      const ticketId = params.get('ticketId') || '';
      const title = params.get('title') || '--';
      const state = params.get('state') || 'Active';
      const ticketLink = params.get('link') || '';
      const createdOn = params.get('createdOn') || '';
      const source = params.get('source') || 'SNOW';

      return (
        <TicketCard
          ticketId={ticketId}
          state={state}
          title={title}
          ticketLink={ticketLink}
          createdOn={createdOn}
          source={source}
        />
      );
    }

    if (href && href.startsWith('create-ticket-action://')) {
      const queryString = href.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      const shortDesc = params.get('shortDesc') || '';

      // WI-1249082: Mark ref that button rendered (useEffect below syncs to module flag)
      buttonRenderedRef.current = true;
      return (
        <button
          style={{
            backgroundColor: '#0078D4',
            color: '#ffffff',
            padding: '6px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            marginTop: '8px',
            display: 'block',
            width: '100%',
            textAlign: 'center' as const
          }}
          onClick={() => {
            // WI-1249082: Clear flag — user chose ticket, so this is NOT a deflection success
            setSelfHelpButtonShown(false);
            // AC7: Emit telemetry — user chose to create ticket after self-help
            getAppInsights().trackEvent({
              name: 'SELF_HELP_DEFLECTION_FAILED',
              properties: {
                shortDescription: shortDesc,
                timestamp: new Date().toISOString(),
              }
            });
            window.dispatchEvent(new CustomEvent('open-form-panel', {
              detail: { shortDesc }
            }));
            // WI-1249082: Show confirmation message in copilot chat
            window.dispatchEvent(new CustomEvent('copilot-system-message', {
              detail: { content: 'Ticket creation started. Please submit the form on the left to proceed.' }
            }));
          }}
        >
          {children}
        </button>
      );
    }

    // Guidance prompt button — sends the prompt text to the agent chat
    if (href && href.startsWith('send-prompt-action://')) {
      const queryString = href.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      const prompt = params.get('prompt') || '';
      let clicked = false;

      return (
        <button
          type="button"
          style={{
            backgroundColor: '#0078D4',
            color: '#ffffff',
            padding: '6px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            marginTop: '8px',
            display: 'block',
            width: '100%',
            textAlign: 'center' as const
          }}
          onClick={(e) => {
            if (clicked) return;
            clicked = true;
            (e.currentTarget as HTMLButtonElement).disabled = true;
            (e.currentTarget as HTMLButtonElement).style.opacity = '0.6';
            window.dispatchEvent(new CustomEvent('copilot-send-message', {
              detail: { message: prompt }
            }));
          }}
          aria-label={`Send prompt: ${prompt}`}
        >
          {children}
        </button>
      );
    }

    return (
      <a className={styles.link} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  };

  return (
    <div className={styles.markdownMessage + ' ' + 'markdownMessage'} ref={containerRef}>
      {segments.map((segment, idx) =>
        segment.type === 'banner' ? (
          <div key={idx} className={styles.howToRespondBanner}>
            <ReactMarkdown components={{ a: LinkRenderer }} remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitizePlugin]} urlTransform={customUrlTransform}>
              {segment.text}
            </ReactMarkdown>
          </div>
        ) : (
          <ReactMarkdown key={idx} components={{ a: LinkRenderer }} remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitizePlugin]} urlTransform={customUrlTransform}>
            {segment.text}
          </ReactMarkdown>
        )
      )}
      {shouldRenderTicketGuidanceBanner && (
        <div className={styles.howToRespondBanner}>
          <ReactMarkdown components={{ a: LinkRenderer }} remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitizePlugin]} urlTransform={customUrlTransform}>
            {normalizedTicketGuidance}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default MarkdownMessage;

// Generated by Copilot
// updated on: 2026-02-26 | 43 lines added/updated | Auto Generated | Fix multi-intent grey banner: replaced single responseBannerMarkdown split with segments-based parser so each "Need more help?" block is greyed independently without greying answer content between them | Time Taken 10 min
// Generated by Copilot
// updated on: 2026-07-21 | 4 lines added/updated | [Auto Generated] | US 1280933 AC-3 (#12996): harden markdown pipeline with dependency-free rehypeSanitizePlugin alongside rehypeRaw to neutralize XSS | Time Taken 6 min
